import {CancelShoot} from '../../components/cancel-shoot.tsx'
import {PullShoots} from '../../components/pull-shoots'
import {CHATBOT_SOURCES, CHATBOT_ASSIGNMENTS_TASK_STATUSES} from '../../types'
import {useChatbotStore} from '../../store/chatbotStore'
import i18next from 'i18next'
import {noShow} from './noShow'
import {notReady} from './notReady'
import {reassign} from './reassign'
import {hostCancel} from './hostCancel.tsx'

export const mb14FlowSteps = (isInShootPage: boolean) => [
    {
        id: 'MB1.4',
        message: i18next.t('chatbot:mb14:starting'),
        trigger: () => {
            useChatbotStore.getState().addStepToHistory({
                id: 'MB1.4',
                source: CHATBOT_SOURCES.CHATBOT,
                text: 'Please select the shoot you wish to cancel'
            })
            return 'MB1.4_pull'
        }
    },
    {
        id: 'MB1.4_pull',
        component: (
            <PullShoots
                flowSource="MB1.4_pull"
                statuses={[
                    CHATBOT_ASSIGNMENTS_TASK_STATUSES.ACCEPTED,
                    CHATBOT_ASSIGNMENTS_TASK_STATUSES.SCHEDULED,
                    CHATBOT_ASSIGNMENTS_TASK_STATUSES.PENDING_ASSETS
                ]}
                filterByDate="0"
            />
        )
    },
    {
        id: 'MB1.4.1',
        message: i18next.t('chatbot:mb14:mb1.4.1'),
        trigger: () => {
            useChatbotStore.getState().addStepToHistory({
                id: 'MB1.4.1',
                source: CHATBOT_SOURCES.CHATBOT,
                text: 'Please select the cancellation reason'
            })
            return 'MB1.4_options'
        }
    },
    {
        id: 'MB1.4_options',
        options: [
            {
                value: '13 host',
                label: i18next.t('chatbot:cancellation_reasons:host_not_show_up'),
                trigger: () => {
                    useChatbotStore.getState().setCancellationReason({
                        text: i18next.t('chatbot:cancellation_reasons:host_not_show_up'),
                        id: '13 host'
                    })
                    useChatbotStore.getState().addStepToHistory({
                        id: 'MB1.4_options',
                        source: CHATBOT_SOURCES.USER,
                        text: 'Host did not show up'
                    })
                    return 'MB1.4_noshow'
                }
            },
            {
                value: 'step;MB1.4_unresponsive',
                label: i18next.t('chatbot:cancellation_reasons:host_unresponsive_never_responded'),
                trigger: () => {
                    useChatbotStore.getState().addStepToHistory({
                        id: 'MB1.4_options',
                        source: CHATBOT_SOURCES.USER,
                        text: 'The Host is unresponsive or never responded'
                    })
                    return 'MB1.3.1'
                }
            },
            {
                value: 'step;MB1.4_notready',
                label: i18next.t('chatbot:cancellation_reasons:host_not_ready_shoot_yet'),
                trigger: () => {
                    useChatbotStore.getState().setCancellationReason({
                        text: i18next.t('chatbot:cancellation_reasons:host_not_ready_shoot_yet'),
                        id: 'host_not_ready'
                    })
                    useChatbotStore.getState().addStepToHistory({
                        id: 'MB1.4_options',
                        source: CHATBOT_SOURCES.USER,
                        text: 'The Host is not ready to shoot yet'
                    })
                    return 'MB1.4_notready'
                }
            },
            {
                value: 'step;MB1.4_reassign',
                label: i18next.t('chatbot:cancellation_reasons:reassign_to_another_photographer'),
                trigger: () => {
                    useChatbotStore.getState().addStepToHistory({
                        id: 'MB1.4_options',
                        source: CHATBOT_SOURCES.USER,
                        text: 'Reassign the photoshoot to another photographer'
                    })
                    return 'MB1.4_reassign'
                }
            },
            {
                value: 'step;MB1.4_host_cancel',
                label: i18next.t('chatbot:cancellation_reasons:host_wants_cancel'),
                trigger: () => {
                    useChatbotStore.getState().addStepToHistory({
                        id: 'MB1.4_options',
                        source: CHATBOT_SOURCES.USER,
                        text: 'The Host wants to cancel'
                    })
                    return 'MB1.4_host_cancel'
                }
            },
            {
                value: 'other',
                label: i18next.t('chatbot:commons:previous_options'),
                trigger: () => {
                    useChatbotStore.getState().addStepToHistory({
                        id: 'MB1.4_options',
                        source: CHATBOT_SOURCES.USER,
                        text: '⬅️ Previous options'
                    })
                    return isInShootPage ? 'start_specific' : 'start_generic'
                }
            }
        ]
    },
    // Final steps for cancellation
    {
        id: 'MB1.4_notes',
        message: () => {
            const cancellationReason = useChatbotStore.getState().cancellationReason
            return i18next.t('chatbot:commons:confirm_reason', {
                cancellationReason: cancellationReason?.text || ''
            })
        },
        trigger: () => {
            const cancellationReason = useChatbotStore.getState().cancellationReason
            useChatbotStore.getState().addStepToHistory({
                id: 'MB1.4_notes',
                source: CHATBOT_SOURCES.CHATBOT,
                text: `Please confirm you want to cancel this shoot because of "${cancellationReason?.text || ''}"`
            })
            return 'MB1.4_notes_options'
        }
    },
    {
        id: 'MB1.4_notes_options',
        options: [
            {
                value: 'confirm',
                label: i18next.t('chatbot:commons:confirm_cancellation'),
                trigger: () => {
                    useChatbotStore.getState().addStepToHistory({
                        id: 'MB1.4_notes_options',
                        source: CHATBOT_SOURCES.USER,
                        text: 'Confirm cancellation'
                    })
                    return 'MB1.4_cancel'
                }
            },
            {
                value: 'abandon',
                label: i18next.t('chatbot:commons:changed_mind'),
                trigger: () => {
                    useChatbotStore.getState().addStepToHistory({
                        id: 'MB1.4_notes_options',
                        source: CHATBOT_SOURCES.USER,
                        text: "I've changed my mind"
                    })
                    return isInShootPage ? '3' : 'start_generic'
                }
            }
        ]
    },
    {
        id: 'MB1.4_cancel',
        component: <CancelShoot flowSource="MB1.4_pull" />,
        asMessage: true,
        replace: true,
        trigger: 'MB1.4_cancel_sent'
    },
    {
        id: 'MB1.4_cancel_sent',
        message: i18next.t('chatbot:mb14:mb1.4_cancel_sent'),
        trigger: () => {
            useChatbotStore.getState().addStepToHistory({
                id: 'MB1.4_cancel_sent',
                source: CHATBOT_SOURCES.CHATBOT,
                text: 'Thank you for letting us know the reason of the cancellation. We can confirm the photoshoot request has been canceled.'
            })
            return 'send_log'
        }
    },
    ...noShow(isInShootPage),
    ...notReady(isInShootPage),
    ...reassign(isInShootPage),
    ...hostCancel(isInShootPage),

    // Final steps for cancellation
    {
        id: 'MB1.4_confirm',
        message: () => {
            const cancellationReason = useChatbotStore.getState().cancellationReason
            return i18next.t('chatbot:commons:confirm_reason', {
                cancellationReason: cancellationReason?.text || ''
            })
        },
        trigger: () => {
            const cancellationReason = useChatbotStore.getState().cancellationReason

            useChatbotStore.getState().addStepToHistory({
                id: 'MB1.4_confirm',
                source: CHATBOT_SOURCES.CHATBOT,
                text: `Please confirm you want to cancel this shoot because of "${cancellationReason?.text || ''}"`
            })
            return 'MB1.4_confirm_options'
        }
    },
    {
        id: 'MB1.4_confirm_options',
        options: [
            {
                value: 'confirm',
                label: i18next.t('chatbot:commons:confirm_cancellation'),
                trigger: () => {
                    useChatbotStore.getState().addStepToHistory({
                        id: 'MB1.4_confirm_options',
                        source: CHATBOT_SOURCES.USER,
                        text: 'Confirm cancellation'
                    })
                    return 'MB1.4_cancel'
                }
            },
            {
                value: 'abandon',
                label: i18next.t('chatbot:commons:changed_mind'),
                trigger: () => {
                    useChatbotStore.getState().addStepToHistory({
                        id: 'MB1.4_confirm_options',
                        source: CHATBOT_SOURCES.USER,
                        text: "I've changed my mind"
                    })
                    return isInShootPage ? '3' : 'start_generic'
                }
            }
        ]
    }
]
